// Dograh headless voice integration.
//
// Replaces the old Web Speech API + hotword + Dialogflow + Google TTS stack.
// Dograh now owns the whole loop: mic capture, STT (Sarvam saaras:v3, language
// auto-detect), the LLM, and TTS (Sarvam bulbul:v3). Bengali / Hindi / English
// are detected per utterance by the STT, so there is no language switch here.
//
// Two things the widget's public API does NOT give us, and how this file covers
// them (see README-DOGRAH.md for the reasoning):
//
//   1. No "agent is speaking" event. The face's mouth needs one. We attach a
//      Web Audio AnalyserNode to the widget's audio element and derive speaking
//      state from output amplitude — which also gives real lip-sync (loudness
//      and, from the spectrum, the mouth shape) rather than a plain on/off.
//   2. No transcript event for voice widgets. Subtitles cannot be driven from
//      the widget; see getTranscriptSupported() below.

import { visemeFromSpectrum } from "../../screens/HomePage/Face/engine/viseme";

const WIDGET_SCRIPT_ID = "dograh-widget";

let analyser = null;
let audioCtx = null;
let rafId = null;

/** Resolves once window.DograhWidget exists. */
function waitForWidget(timeoutMs = 15000) {
	return new Promise((resolve, reject) => {
		const started = Date.now();
		const poll = () => {
			if (window.DograhWidget) return resolve(window.DograhWidget);
			if (Date.now() - started > timeoutMs) {
				return reject(new Error("Dograh widget script did not load in time"));
			}
			setTimeout(poll, 100);
		};
		poll();
	});
}

/**
 * Injects the widget script. The src must be the dashboard-generated URL, which
 * carries the embed token — put it in REACT_APP_DOGRAH_WIDGET_SRC.
 */
export function loadDograhWidget(context = {}) {
	if (document.getElementById(WIDGET_SCRIPT_ID)) return waitForWidget();

	const src = process.env.REACT_APP_DOGRAH_WIDGET_SRC;
	if (!src) {
		return Promise.reject(
			new Error("REACT_APP_DOGRAH_WIDGET_SRC is not set. Copy it from the agent's Configure Widget dialog.")
		);
	}

	const js = document.createElement("script");
	js.id = WIDGET_SCRIPT_ID;
	js.src = src;
	js.async = true;
	js.setAttribute(
		"data-dograh-context",
		JSON.stringify({
			surface: "robot-misti",
			page_url: window.location.href,
			...context,
		})
	);
	document.body.appendChild(js);

	return waitForWidget();
}

/**
 * Watches the widget's audio output and reports speaking state, amplitude and
 * mouth shape.
 *
 * The widget renders its remote audio into an <audio> element it owns. We find
 * it, tap it with an AnalyserNode, and on each animation frame measure RMS and
 * read the spectrum, which visemeFromSpectrum turns into a mouth shape.
 *
 * onSpeaking(isSpeaking, level, shape)
 *   level  0..1 loudness
 *   shape  { open, wide } 0..1 - how far the jaw drops, and wide ("ee")
 *          versus round ("oo"); see Face/engine/viseme.js
 */
export function startSpeakingDetector(onSpeaking, { threshold = 0.02, holdMs = 180 } = {}) {
	stopSpeakingDetector();

	const attach = () => {
		// The widget creates its own audio element for the remote track.
		const el = Array.from(document.querySelectorAll("audio")).find((a) => a.srcObject || a.src);
		if (!el) return false;

		try {
			audioCtx = new (window.AudioContext || window.webkitAudioContext)();
			const source = el.srcObject
				? audioCtx.createMediaStreamSource(el.srcObject)
				: audioCtx.createMediaElementSource(el);

			analyser = audioCtx.createAnalyser();
			// 2048 gives ~23 Hz bins at 48 kHz - fine enough to find the vowel
			// formants the mouth shape comes from. A little smoothing takes the
			// flicker out of the spectrum without lagging behind the syllables.
			analyser.fftSize = 2048;
			analyser.smoothingTimeConstant = 0.35;
			source.connect(analyser);
			// A stream source only listens - the element still plays it. An element
			// source takes the element's output over, so it has to be passed on.
			if (!el.srcObject) source.connect(audioCtx.destination);

			const data = new Uint8Array(analyser.fftSize);
			const freq = new Uint8Array(analyser.frequencyBinCount);
			const binHz = audioCtx.sampleRate / analyser.fftSize;
			let lastLoud = 0;
			let wasSpeaking = false;

			const tick = () => {
				analyser.getByteTimeDomainData(data);
				let sum = 0;
				for (let i = 0; i < data.length; i++) {
					const v = (data[i] - 128) / 128;
					sum += v * v;
				}
				const rms = Math.sqrt(sum / data.length);
				const now = Date.now();
				if (rms > threshold) lastLoud = now;

				// Hold briefly so natural pauses between words don't shut the mouth.
				const speaking = now - lastLoud < holdMs;
				if (speaking !== wasSpeaking || speaking) {
					wasSpeaking = speaking;
					const level = Math.min(1, rms * 8);
					let shape = null;
					if (speaking) {
						analyser.getByteFrequencyData(freq);
						shape = visemeFromSpectrum(freq, binHz, level);
					}
					onSpeaking(speaking, level, shape);
				}
				rafId = requestAnimationFrame(tick);
			};
			tick();
			return true;
		} catch (err) {
			console.error("Dograh speaking detector failed to attach:", err);
			return false;
		}
	};

	// The audio element appears only once the call connects, so retry briefly.
	let tries = 0;
	const retry = () => {
		if (attach()) return;
		if (tries++ < 50) setTimeout(retry, 200);
	};
	retry();
}

export function stopSpeakingDetector() {
	if (rafId) cancelAnimationFrame(rafId);
	rafId = null;
	if (audioCtx && audioCtx.state !== "closed") audioCtx.close().catch(() => {});
	audioCtx = null;
	analyser = null;
}

/**
 * Voice widgets expose no transcript callback — onMessage is chat-only.
 * Subtitles therefore cannot come from the widget. Read transcripts from the
 * run afterwards via the Dograh API, or run a chat widget alongside.
 */
export function getTranscriptSupported() {
	return false;
}


/**
 * Mutes or unmutes the microphone the widget is sending to Dograh, without
 * touching the call itself.
 *
 * This is how the wake word gates a session that is already connected: the
 * WebRTC connection stays up, so there is no setup delay when someone speaks,
 * but the agent hears nothing until the mic is opened.
 *
 * Note this sets track.enabled rather than stopping the track. The track keeps
 * transmitting (silence), so the connection stays healthy - but Dograh's VAD
 * sees no speech and therefore runs no STT, LLM or TTS.
 *
 * @returns {boolean} false if there is no live stream yet
 */
export function setMicEnabled(enabled) {
	const state = window.DograhWidget && window.DograhWidget.getState && window.DograhWidget.getState();
	const stream = state && state.stream;
	if (!stream || !stream.getAudioTracks) return false;

	const tracks = stream.getAudioTracks();
	if (!tracks.length) return false;

	tracks.forEach((t) => {
		t.enabled = enabled;
	});
	console.log(`[mic] ${enabled ? "open - Misti is listening" : "closed - waiting for wake word"}`);
	return true;
}

/** True when the widget currently has a live mic track that is enabled. */
export function isMicEnabled() {
	const state = window.DograhWidget && window.DograhWidget.getState && window.DograhWidget.getState();
	const stream = state && state.stream;
	if (!stream || !stream.getAudioTracks) return false;
	const t = stream.getAudioTracks()[0];
	return !!(t && t.enabled);
}

/**
 * Silences Misti's conversational voice without touching the call. Used while
 * she says a line sent from the phone app, so the two voices never overlap.
 */
export function setAgentAudioMuted(muted) {
	const state = window.DograhWidget && window.DograhWidget.getState && window.DograhWidget.getState();
	const el = state && state.audioElement;
	if (!el) return false;
	el.muted = muted;
	return true;
}

const dograh = {
	loadDograhWidget,
	startSpeakingDetector,
	stopSpeakingDetector,
	getTranscriptSupported,
	setMicEnabled,
	isMicEnabled,
	setAgentAudioMuted,
};

export default dograh;
